import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/app/hooks';
import { useUpdateSireMutation } from '@/app/services/donorSchedules';
import { addToast } from '@/app/slices/toast';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { SireSaveWarningDialog } from '@/components/common/sire-save-warning-dialog';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { cn } from '@/lib/utils';
import { Sire } from '@/rtk-query.gql';
import { CurrentUser, hasPermission, useAccessToken } from '@/services/auth';
import { breedByIdOptions } from '@/services/lookups';
import { BreedResponse } from '@/services/lookups/types';
import {
  useSireQuery,
  useUpdateSireMutation as useUpdateSireRestMutation,
} from '@/services/sire';
import { SireDetail } from '@/services/sire/types';
import { verifiedOptions } from '@/types/common';

import { BreedsAutoComplete } from '../breeds-auto-complete';
import { BreedsCombobox } from '../breeds-combobox';
import { SpeciesDropdown } from '../species-dropdown/SpeciesDropdown';
import { editSireFormSchema, EditSireFormValues } from './schema';

export type { EditSireFormValues } from './schema';

type IEditSireFormProps = {
  onCancel: () => void;
  sire?: Sire;
  onSuccess?: (data: EditSireFormValues) => void;
  onError?: (error: unknown) => void;
  calendarContainer?: HTMLElement;
};

export const EditSireForm = ({
  onCancel,
  sire,
  onSuccess,
  onError,
  calendarContainer,
}: IEditSireFormProps) => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { me } = useAppSelector((s) => s.authentication);
  const [updateSireMutation] = useUpdateSireMutation();
  const isSireVerifiedEnabled = useFeatureFlag('Sire_Verified_Field');
  const isSireEditRefreshEnabled = useFeatureFlag('Sire_Edit_Refresh');
  const isSaveWarningEnabled = useFeatureFlag('Sire_Save_Warning');
  const isBreedComboboxEnabled = useFeatureFlag(
    'Account_Combobox_not_Dropdown',
  );
  const [pendingSave, setPendingSave] = useState<EditSireFormValues | null>(
    null,
  );
  const { mutateAsync: updateSireRest } = useUpdateSireRestMutation();
  const { data: sireDetail } = useSireQuery(
    sire?.id,
    isSireVerifiedEnabled || isSireEditRefreshEnabled,
  );
  const freshSireDetail =
    isSireEditRefreshEnabled && sireDetail && sireDetail.id === sire?.id
      ? sireDetail
      : undefined;
  const formBreed = (freshSireDetail ? freshSireDetail.breed : sire?.breed) as
    | BreedResponse
    | null
    | undefined;
  const [selectedSubFamilies, setSelectedSubFamilies] = useState<number[]>([]);
  const [breedResetKey, setBreedResetKey] = useState(0);

  const toFormValues = (s?: Sire | SireDetail): EditSireFormValues => ({
    sireId: s?.id || '',
    name: s?.name || '',
    breedId: s?.breed?.id || '',
    breedReg: s?.breedReg || '',
    registrationNumber: s?.registrationNumber || '',
    regPrefix: s?.regPrefix || '',
    cane: s?.cane || '',
    birth: s?.birth ? new Date(s.birth) : null,
    sire: s?.sire || '',
    heparin: s?.heparin || undefined,
    verified: undefined,
  });

  const formBreedId = formBreed?.id;
  const { data: breedDetail, isError: isBreedDetailError } = useQuery({
    ...breedByIdOptions(token, formBreedId ?? ''),
    enabled: !!token && !!formBreedId,
  });
  const initialSubFamily =
    !formBreedId || (!breedDetail && !isBreedDetailError)
      ? undefined
      : (breedDetail?.subFamily ?? null);
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getFieldState,
    formState: { errors },
  } = useForm<EditSireFormValues>({
    resolver: zodResolver(editSireFormSchema),
    defaultValues: toFormValues(sire),
  });

  useEffect(() => {
    reset(toFormValues(sire));
    setBreedResetKey(0);
    setSelectedSubFamilies([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sire?.id, sire?.breed?.id]);

  useEffect(() => {
    if (sireDetail && sireDetail.id === sire?.id) {
      setValue('verified', sireDetail.verified ? 'Yes' : 'No');
    }
  }, [sireDetail, sire?.id, setValue]);

  useEffect(() => {
    if (!freshSireDetail) return;
    const hasUserEdits = (
      Object.keys(toFormValues()) as (keyof EditSireFormValues)[]
    ).some((name) => getFieldState(name).isDirty);
    if (hasUserEdits) return;

    reset({
      ...toFormValues(freshSireDetail),
      ...(isSireVerifiedEnabled && {
        verified: freshSireDetail.verified ? 'Yes' : 'No',
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freshSireDetail]);

  const getFormErrorMessage = (name: keyof EditSireFormValues) => {
    return (
      errors[name] && (
        <small className="mt-1 text-sm text-red-500">
          {errors[name]?.message}
        </small>
      )
    );
  };

  const onSubmit = async (data: EditSireFormValues) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const { verified, ...input } = data;
      if (isSireVerifiedEnabled) {
        const { sireId, ...request } = input;
        await updateSireRest({
          sireId,
          request: {
            ...request,
            ...(verified && { verified: verified === 'Yes' }),
          },
        });
      } else {
        await updateSireMutation({ input });
      }

      await queryClient.refetchQueries({
        queryKey: ['semen'],
      });

      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.success'),
          detail: t('sire.sireUpdatedSuccessfully'),
          life: ToastLife.MEDIUM,
        }),
      );

      onSuccess?.(data);
      onCancel();
    } catch (error) {
      console.error('Failed to update sire:', error);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: t('sire.failedToUpdateSire'),
          life: ToastLife.MEDIUM,
        }),
      );
      onError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitRequested = async (data: EditSireFormValues) => {
    if (isSaveWarningEnabled) {
      setPendingSave(data);
      return;
    }
    await onSubmit(data);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const isSireEditDisabled = (me: CurrentUser | undefined) => {
    if (!me) return true;
    return !hasPermission(me, 'SEMEN_APP.EDIT_SIRE');
  };

  return (
    <form onSubmit={handleSubmit(onSubmitRequested)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="label-input-group">
          <label htmlFor="name" className="required">
            {t('sire.sireName')}
          </label>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                id="name"
                {...field}
                disabled={isSireEditDisabled(me)}
                className="disabled:font-medium disabled:text-[#5e81ac] disabled:opacity-45"
                tabIndex={1}
                aria-invalid={fieldState.invalid}
              />
            )}
          />
          {getFormErrorMessage('name')}
        </div>

        <SpeciesDropdown
          label={t('fields.species', 'Species')}
          disabled={isSireEditDisabled(me)}
          initialSubFamily={initialSubFamily ?? null}
          tabIndex={2}
          onChange={(subFamilies) => {
            setSelectedSubFamilies(subFamilies);
          }}
          onUserChange={() => {
            setBreedResetKey((k) => k + 1);
            setValue('breedId', '');
          }}
        />

        <div className="label-input-group">
          <Controller
            name="breedId"
            control={control}
            render={({ field, fieldState }) =>
              isBreedComboboxEnabled ? (
                <>
                  <label htmlFor="breedId">{`${t('fields.breed')}*`}</label>
                  <BreedsCombobox
                    key={breedResetKey}
                    id="breedId"
                    value={field.value || undefined}
                    container={calendarContainer}
                    subFamilies={
                      selectedSubFamilies.length > 0
                        ? selectedSubFamilies
                        : undefined
                    }
                    disabled={isSireEditDisabled(me)}
                    invalid={fieldState.invalid}
                    tabIndex={3}
                    onChange={(breed) => {
                      field.onChange(breed?.id ?? '');
                    }}
                  />
                </>
              ) : (
                <BreedsAutoComplete
                  key={breedResetKey}
                  defaultValue={
                    breedResetKey === 0 ? (formBreed ?? undefined) : undefined
                  }
                  label={`${t('fields.breed')}*`}
                  className={cn('w-full', fieldState.invalid && 'p-invalid')}
                  subFamilies={
                    selectedSubFamilies.length > 0
                      ? selectedSubFamilies
                      : undefined
                  }
                  disabled={isSireEditDisabled(me)}
                  tabIndex={3}
                  onSelect={(e: AutoCompleteSelectEvent) => {
                    field.onChange(e.value?.id);
                  }}
                />
              )
            }
          />
          {getFormErrorMessage('breedId')}
        </div>

        <div className="label-input-group">
          <label htmlFor="breedReg">{t('fields.breedRegistration')}</label>
          <Controller
            name="breedReg"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                id="breedReg"
                {...field}
                tabIndex={4}
                aria-invalid={fieldState.invalid}
              />
            )}
          />
          {getFormErrorMessage('breedReg')}
        </div>

        <div className="label-input-group">
          <label htmlFor="registrationNumber">
            {t('fields.registrationNumber')}
          </label>
          <Controller
            name="registrationNumber"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                id="registrationNumber"
                {...field}
                tabIndex={5}
                aria-invalid={fieldState.invalid}
              />
            )}
          />
          {getFormErrorMessage('registrationNumber')}
        </div>

        <div className="label-input-group">
          <label htmlFor="regPrefix">{t('fields.countryCode')}</label>
          <Controller
            name="regPrefix"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                id="regPrefix"
                {...field}
                tabIndex={6}
                aria-invalid={fieldState.invalid}
              />
            )}
          />
          {getFormErrorMessage('regPrefix')}
        </div>

        <div className="label-input-group">
          <label htmlFor="cane">{t('semen.cane')}</label>
          <Controller
            name="cane"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                id="cane"
                {...field}
                tabIndex={7}
                aria-invalid={fieldState.invalid}
              />
            )}
          />
          {getFormErrorMessage('cane')}
        </div>

        <div className="label-input-group">
          <label htmlFor="birth">{t('fields.birthDate')}</label>
          <Controller
            name="birth"
            control={control}
            render={({ field, fieldState }) => (
              <Calendar
                id="birth"
                value={field.value}
                onChange={(e) => field.onChange(e.value as Date)}
                dateFormat="mm/dd/yy"
                showIcon
                tabIndex={8}
                className={cn('w-full', fieldState.invalid && 'p-invalid')}
                container={calendarContainer}
              />
            )}
          />
          {getFormErrorMessage('birth')}
        </div>

        <div className="label-input-group">
          <label htmlFor="sire">{t('fields.pedigreeName')}</label>
          <Controller
            name="sire"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                id="sire"
                {...field}
                tabIndex={9}
                aria-invalid={fieldState.invalid}
              />
            )}
          />
          {getFormErrorMessage('sire')}
        </div>

        <div className="label-input-group">
          <label htmlFor="heparin">{t('semen.heparin')}</label>
          <Controller
            name="heparin"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                id="heparin"
                type="number"
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? undefined : Number(e.target.value),
                  )
                }
                tabIndex={10}
                aria-invalid={fieldState.invalid}
              />
            )}
          />
          {getFormErrorMessage('heparin')}
        </div>

        {isSireVerifiedEnabled && (
          <div className="label-input-group">
            <label htmlFor="verified">{t('sire.verified')}</label>
            <Controller
              name="verified"
              control={control}
              render={({ field }) => (
                <Select
                  items={verifiedOptions}
                  value={field.value ?? null}
                  onChange={(value) => {
                    if (value) field.onChange(value);
                  }}
                >
                  <SelectTrigger
                    id="verified"
                    data-testid="verified"
                    className="w-full"
                    tabIndex={11}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {verifiedOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {t('common.save')}
        </Button>
      </div>
      <SireSaveWarningDialog
        open={pendingSave !== null}
        mode="edit"
        onCancel={() => setPendingSave(null)}
        onConfirm={() => {
          const values = pendingSave;
          setPendingSave(null);
          if (values) {
            void onSubmit(values);
          }
        }}
      />
    </form>
  );
};
